import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, NgForm, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ApFindControlObjectValue, ApFindControlService, createFindControlObjectValue } from 'webcommon/find-control';
import { FindControlIdService, WebCommonStorageRepository } from 'webcommon/legacy-common';
import {
  FindControlListMacroGroup,
  FindControlRequiredSearchParametersDto,
  IColumnPk,
  ImoTermDto,
  MacroAddDxDto,
  MacroAddProcedureDto,
  MacroDataSourceEnum,
  MacroDefinitionDto,
  MacroGroupDto,
} from 'webcommon/shared';
import { MacroService } from '../ap-macro/index';

@Component({
  selector: 'ap-macro-editor-details',
  styleUrls: ['./ap-macro-editor-details.component.less'],
  templateUrl: './ap-macro-editor-details.component.html',
})
export class ApMacroEditorDetailsComponent implements OnInit {
  @Input() baseId: string;
  @Input() macroService: MacroService;
  @Input() macroDefinitionDto: MacroDefinitionDto;
  @Input() defaultMacroGroup: MacroGroupDto | null;
  @Input() allowedMacroGroups: string[];
  @ViewChild('form', { static: true }) ngForm: NgForm;
  @ViewChild('goalTextArea', { static: true }) goalTextArea: ElementRef;

  allowDxMultiSelect = true;
  contextId: string;
  dataSource: MacroDataSourceEnum;
  description: string;
  findControlSearchParams: FindControlRequiredSearchParametersDto = {
    RequiredSearchParameters: [
      {
        ColumnName: 'UserUid',
        Value: this.getDefaultUserId(),
      },
    ],
  };
  Id: string;
  goal: string;
  groups: string[] = [];
  contents: string[];
  disableVisitFields = false;
  disablePatientFields = false;
  selectedMacroGroupId = '';

  macroDiagnosis: ApFindControlObjectValue<ImoTermDto> = null;
  socodes: string[] = [];
  spcodes: string[] = [];

  goalError: string | null = null;
  goalEnabled = true;
  groupError: string | null = null;

  patientRequired: boolean;
  patientRequiredDisabled = false;
  showPatientRequiredCheckbox = false;

  patientId: string | null = null;

  constructor(
    public findControlIdService: FindControlIdService,
    private webCommonStorageRepository: WebCommonStorageRepository,
    private apFindControlService: ApFindControlService
  ) {}

  ngOnInit() {
    this.setId();
    this.getSOs();
    this.getSPs();
    this.getMacroDiagnosis();
    this.getMacroGroup();
    this.validateGoalCriteria();
    this.setupVisitId();
    this.setupPatientId();
    this.showPatientRequiredCheckboxIfEnabled();
  }

  private getSOs() {
    if (this.macroDefinitionDto.AddSOs && this.macroDefinitionDto.AddSOs.length > 0) {
      this.macroDefinitionDto.AddSOs.forEach((procedure) => this.socodes.push(procedure.ProcedureId));
    }
  }

  private getSPs() {
    if (this.macroDefinitionDto.AddSPs && this.macroDefinitionDto.AddSPs.length > 0) {
      this.macroDefinitionDto.AddSPs.forEach((procedure) => this.spcodes.push(procedure.ProcedureId));
    }
  }

  private getMacroDiagnosis() {
    if (this.macroDefinitionDto) {
      this.macroDiagnosis = createFindControlObjectValue(this.macroDefinitionDto.AddDxs.map((dx) => this.mapToImoTerm(dx)));
    } else {
      this.macroDiagnosis = null;
    }
  }

  private getMacroGroup() {
    if (this.macroDefinitionDto && this.macroDefinitionDto.MacroGroup && this.macroDefinitionDto.MacroGroup.MacroGroupId) {
      this.groups = [this.macroDefinitionDto.MacroGroup.MacroGroupId];
    } else {
      this.groups = [];
      if (this.defaultMacroGroup) {
        this.macroDefinitionDto.MacroGroup = this.defaultMacroGroup;
        this.groups = [this.defaultMacroGroup.MacroGroupId];
      }
    }
  }

  private setupVisitId() {
    const visitId = this.macroService.getVisitId();
    if (!visitId || visitId === '') {
      this.disableVisitFields = true;
      this.goalEnabled = false;
    }
  }

  private setupPatientId() {
    this.patientId = this.macroService.getPatientId();
    if (!this.macroDefinitionDto.IsPatientRequired || !this.patientId || this.patientId === '') {
      this.disablePatientFields = true;
    }
  }

  private showPatientRequiredCheckboxIfEnabled() {
    if (this.showPatientRequiredCheckbox) {
      if (this.macroDefinitionDto.Id) {
        this.patientRequiredDisabled = true;
        this.patientRequired = this.macroDefinitionDto.IsPatientRequired;
      }

      if (!this.macroDefinitionDto.Id) {
        this.patientRequiredDisabled = false;
        this.patientRequired = this.patientId ? true : false;
      }
    }
  }
  private mapToImoTerm(dx: MacroAddDxDto): ImoTermDto {
    return {
      UniqueCode: dx.LexicalCode,
      LexicalCode: dx.LexicalCode,
      PrimaryDiagnosis: {
        Icd10Code: dx.Code10,
        Description: dx.CodeDescription,
        Icd9Code: null,
        NonSpecificCodeFlag: null,
        Dsm: null,
        Icd10Rafs: [],
        Icd10HccModelCat: null,
        Icd10Description: null,
        PrimaryLexicalCode: dx.LexicalCode,
      },
      SnomedCode: null,
      SecondaryDiagnoses: [],
      RequiresPostCoordination: false,
      Icd10Codes: [dx.Code10],
      AllowsPostCoordination: false,
    };
  }

  private mapToMacroDx(term: ImoTermDto): MacroAddDxDto {
    return {
      Code10: term.PrimaryDiagnosis.Icd10Code || '',
      CodeDescription: term.PrimaryDiagnosis.Description,
      Id: '',
      LexicalCode: term.LexicalCode,
    };
  }

  onChipSelected(data: IColumnPk | null): void {
    if (data) {
      const macroGroup = this.apFindControlService.mapFindControlObject<FindControlListMacroGroup>(data);
      this.macroDefinitionDto.MacroGroup = { MacroGroupId: macroGroup?.MacroGroupUid ?? '', Name: macroGroup?.Name ?? '' };
      this.groupError = null;
    } else {
      this.macroDefinitionDto.MacroGroup = { MacroGroupId: '', Name: '' };
    }
  }

  onCheckboxChanged() {
    this.disablePatientFields = !this.patientRequired;
    this.macroDefinitionDto.IsPatientRequired = this.patientRequired;

    if (this.disablePatientFields) {
      this.socodes = [];
      this.spcodes = [];
      this.macroDefinitionDto.AddSOs = [];
      this.macroDefinitionDto.AddSPs = [];
      this.macroDefinitionDto.AddDxs = [];
      this.macroDefinitionDto.Goal = '';
      this.macroDiagnosis = null;
    }
  }

  isFormValid() {
    return this.ngForm.valid;
  }

  hasGroupError(): boolean {
    return this.groupError != null;
  }

  hasGoalError(): boolean {
    return this.goalError != null;
  }

  isInputValid() {
    this.validateGoalCriteria();
    return !this.hasGroupError() && !this.hasGoalError();
  }

  onSOSelected(procedureIds: string[]) {
    this.macroDefinitionDto.AddSOs = [];
    procedureIds.forEach((id) => this.addSOProcedure(id));
  }

  addSOProcedure(procedureIds: string) {
    const dto = new MacroAddProcedureDto();
    dto.ProcedureId = procedureIds;
    this.macroDefinitionDto.AddSOs.push(dto);
  }

  onSPSelected(procedureIds: string[]) {
    this.macroDefinitionDto.AddSPs = [];
    procedureIds.forEach((id) => this.addSPProcedure(id));
  }

  addSPProcedure(procedureIds: string) {
    const dto = new MacroAddProcedureDto();
    dto.ProcedureId = procedureIds;
    this.macroDefinitionDto.AddSPs.push(dto);
  }

  onDXChipsChanged(value: ApFindControlObjectValue<ImoTermDto>) {
    if (!value) {
      this.macroDefinitionDto.AddDxs = [];
    } else {
      this.macroDefinitionDto.AddDxs = value.getAsList().map((term) => this.mapToMacroDx(term));
    }
    this.validateGoalCriteria();
  }

  private setId() {
    this.Id = `${this.baseId}-details`;
  }

  private getDefaultUserId() {
    let userId = this.webCommonStorageRepository.getUserId();

    if (!userId) {
      userId = '';
    }

    return userId;
  }

  private validateGoalCriteria(): void {
    this.goalError = null;
    // if (this.macroDefinitionDto.AddDxs.length === 1 || this.macroDefinitionDto.Goal) {
    //   this.goalEnabled = true;
    // }
    // if (this.macroDefinitionDto.AddDxs.length !== 1 && !this.macroDefinitionDto.Goal) {
    //   this.goalEnabled = false;
    // }
    if (this.macroDefinitionDto.AddDxs.length === 0 && this.macroDefinitionDto.Goal) {
      this.goalError = 'A DX Code is required when adding a Goal.';
    }
    if (this.macroDefinitionDto.AddDxs.length > 1 && this.macroDefinitionDto.Goal) {
      this.goalError = 'Only one DX Code is allowed when adding a Goal.';
    }
    if (this.goalError) {
      this.goalTextArea.nativeElement.focus();
    }
  }

  validMacroGroupValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const valid = this.allowedMacroGroups.includes(control.value);
      return !valid ? { macroGroup: { value: control.value } } : null;
    };
  }
}
